<script setup>
/* eslint-disable */
import { onMounted, ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import FinanceAPI from 'dashboard/api/finance';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';
import BillingInvoices from './components/BillingInvoices.vue';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';

const router = useRouter();
const route = useRoute();
const { currentAccount, accountId } = useAccount();
const { t, te } = useI18n();

const todayDate = computed(() => {
  const today = new Date();
  return today.toISOString().split('T')[0];
});

const isIspSku = sku =>
  [
    'sgp_integration',
    'ixc_integration',
    'mkauth_integration',
    'hubsoft_integration',
  ].includes(sku);

const activeIspIntegration = computed(() => {
  return storeModules.value.some(m => isIspSku(m.sku) && m.is_active);
});

const isLoading = ref(true);
const subscriptions = ref([]);
const invoices = ref([]);
const unlinkedTaxInvoices = ref([]);
const prices = ref({});
const billingPreferences = ref('email');
const billingEmail = ref('');
const billingPhone = ref('');
const isSavingPreferences = ref(false);
const currentTab = ref(route.query.tab === 'store' ? 'store' : 'plan'); // 'plan', 'store', 'invoices', 'preferences'
const limits = ref({});
const limitsUsage = ref({});
const aiUsageResetLabel = computed(() => {
  const resetAt = limitsUsage.value?.ai_usage_reset_at;
  if (!resetAt) return t('BILLING.PAGE.STORE.RESET_NEXT_CYCLE');

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date(resetAt));
});
const usagePercentage = usageData => {
  const usage = Number(usageData?.usage || 0);
  const max = Number(usageData?.max || 0);
  if (max <= 0) return usage > 0 ? 100 : 0;
  return Math.min((usage / max) * 100, 100);
};
const usageBarColor = usageData => {
  const percentage = usagePercentage(usageData);
  if (percentage >= 90) return '#ef4444';
  if (percentage >= 75) return '#f59e0b';
  return '#3b82f6';
};
const planName = ref('Carregando...');
const planPrice = ref(0);
const extrasPrice = ref(0);
const planFeatures = ref({});
const availablePlans = ref([]);
const storeModules = ref([]);
const showPlanUpgradeModal = ref(false);

const showUpgradeModal = ref(false);
const showConfirmModal = ref(false);
const confirmModalData = ref({});
const upgradePlanData = ref(null);

const showSendModal = ref(false);
const sendModalData = ref({ type: 'email', target: '', invoice: null });
const isSendingInvoice = ref(false);
const upgradeAutoCancel = ref([]);
const upgradeOptionalCancel = ref([]);
const selectedOptionalCancel = ref([]);

const isSubscribing = ref({});

const fetchFinanceData = async () => {
  isLoading.value = true;
  try {
    const response = await FinanceAPI.get();
    subscriptions.value = response.data.subscriptions;
    invoices.value = response.data.invoices || [];
    unlinkedTaxInvoices.value = response.data.unlinked_tax_invoices || [];
    prices.value = response.data.prices || {};

    limits.value = response.data.limits || {};
    limitsUsage.value = response.data.limits_usage || {};
    planName.value =
      response.data.plan_name || t('BILLING.PAGE.STORE.CUSTOM_PLAN');
    planPrice.value = response.data.plan_price || 0.0;
    planFeatures.value = response.data.plan_features || {};
    availablePlans.value = response.data.available_plans || [];
    storeModules.value = response.data.store_modules || [];

    billingPreferences.value = limits.value.billing_preferences || 'email';
    billingEmail.value =
      limits.value.billing_email || response.data.default_billing_email || '';
    billingPhone.value =
      limits.value.billing_phone || response.data.default_billing_phone || '';

    // Get extras_price directly from backend
    extrasPrice.value = response.data.extras_price || 0;
  } catch (error) {
    useAlert(error.message);
  } finally {
    isLoading.value = false;
  }
};

const quantities = ref({
  inbox_whatsapp: 1,
  inbox_instagram: 1,
  inbox_telegram: 1,
  inbox_facebook: 1,
  agent_extra: 1,
  trigger_words_pack: 1,
  mini_ai_workflow: 1,
  lumerai_analytics_reports_pack: 1,
  ia: 1,
  alerts: 1,
  tickets: 1,
  crm: 1,
  reports: 1,
  sla: 1,
  white_label: 1,
  api: 1,
  campaigns: 1,
  sms_pack_1000: 1,
  sms_pack_5000: 1,
});

const moduleKey = sku => sku.toUpperCase();
const moduleName = sku =>
  te(`BILLING.PAGE.STORE.MODULE_NAMES.${moduleKey(sku)}`)
    ? t(`BILLING.PAGE.STORE.MODULE_NAMES.${moduleKey(sku)}`)
    : '';
const moduleDescription = sku =>
  te(`BILLING.PAGE.STORE.MODULE_DESCRIPTIONS.${moduleKey(sku)}`)
    ? t(`BILLING.PAGE.STORE.MODULE_DESCRIPTIONS.${moduleKey(sku)}`)
    : '';

const moduleDependencies = {
  mini_ai_workflow: ['automations'],
  semi_ia: ['automations'],
  ai_markdown_images: ['ia'],
};

const hasExtraSubscription = sku => {
  return (subscriptions.value || []).some(
    sub =>
      sub.status !== 'INACTIVE' &&
      ((sub.description && sub.description.includes(sku)) ||
        (sub.id && sub.id.includes(sku)))
  );
};

const adjustQuantity = (sku, amount) => {
  if (quantities.value[sku] + amount >= 1) {
    quantities.value[sku] += amount;
  }
};

const handleSubscribe = async sku => {
  if (isSubscribing.value[sku]) return;

  const quantity = quantities.value[sku] || 1;
  const price = prices.value[sku] || 0;
  let total = price * quantity;

  const name = moduleName(sku);

  const deps = moduleDependencies[sku] || [];
  const missingDeps = deps.filter(
    dep => !storeModules.value.find(m => m.sku === dep && m.is_active)
  );

  missingDeps.forEach(dep => {
    total += prices.value[dep] || 0;
  });

  confirmModalData.value = { sku, quantity, name, total, missingDeps };
  showConfirmModal.value = true;
};

const openSendModal = (invoice, type) => {
  showSendModal.value = true;
  sendModalData.value = {
    type,
    target: type === 'email' ? billingEmail.value : billingPhone.value,
    invoice,
  };
};

const sendInvoice = async () => {
  isSendingInvoice.value = true;
  try {
    await FinanceAPI.sendInvoice(
      sendModalData.value.invoice.id,
      sendModalData.value.invoice.invoiceUrl,
      sendModalData.value.type,
      sendModalData.value.target
    );
    useAlert(
      t('BILLING.PAGE.STORE.INVOICE_SENT', {
        type: sendModalData.value.type,
      })
    );
    showSendModal.value = false;
  } catch (error) {
    const serverError = error.response?.data?.error;
    useAlert('Erro ao reenviar fatura: ' + (serverError || error.message));
  } finally {
    isSendingInvoice.value = false;
  }
};

const executeSubscription = async () => {
  const { sku, quantity, missingDeps } = confirmModalData.value;
  showConfirmModal.value = false;

  isSubscribing.value[sku] = true;
  try {
    const response = await FinanceAPI.subscribe(
      sku,
      quantity,
      missingDeps || []
    );
    useAlert(
      `Assinatura criada com sucesso! Verifique seu ${billingPreferences.value} em breve.`
    );
    await fetchFinanceData();

    // Redirect to inbox setup if the user just bought an inbox
    if (sku.startsWith('inbox_')) {
      const channelType = sku.replace('inbox_', '');
      router.push({
        name: 'settings_inboxes_page_channel',
        params: { sub_page: channelType, accountId: accountId.value },
      });
    }
  } catch (error) {
    useAlert(error.message);
  } finally {
    isSubscribing.value[sku] = false;
  }
};

const savePreferences = async () => {
  isSavingPreferences.value = true;
  try {
    await FinanceAPI.savePreferences(
      billingPreferences.value,
      billingEmail.value,
      billingPhone.value
    );
    useAlert(t('BILLING.PAGE.STORE.PREFERENCES_SAVED'));
  } catch (error) {
    useAlert(error.message);
  } finally {
    isSavingPreferences.value = false;
  }
};

const openUpgradeModal = plan => {
  upgradePlanData.value = plan;
  upgradeAutoCancel.value = [];
  upgradeOptionalCancel.value = [];
  selectedOptionalCancel.value = [];

  const planModules = plan.enabled_modules || {};

  const booleanModules = {
    tickets: ['tickets', 'help_center'],
    crm: ['crm'],
    reports: ['reports'],
    api: ['api'],
    sla: ['sla'],
    campaigns: ['campaigns'],
    white_label: ['white_label'],
  };

  const quantityModules = [
    'inbox_whatsapp',
    'inbox_instagram',
    'inbox_telegram',
    'inbox_facebook',
    'agent_extra',
    'trigger_words_pack',
    'calendar_agenda_5',
    'calendar_agenda_10',
    'calendar_agenda_20',
    'mini_ai_workflow',
    'ia',
    'alerts',
  ];

  (subscriptions.value || []).forEach(sub => {
    if (sub.status === 'INACTIVE') return;

    let matchedBoolean = false;
    for (const [sku, keys] of Object.entries(booleanModules)) {
      if (sub.description && sub.description.includes(sku)) {
        const isIncludedInNewPlan = keys.some(key => planModules[key]);
        if (isIncludedInNewPlan) {
          upgradeAutoCancel.value.push({
            id: sub.id,
            description: sub.description,
            value: sub.value,
          });
        }
        matchedBoolean = true;
        break;
      }
    }

    if (!matchedBoolean) {
      const isQuantity = quantityModules.some(
        sku => sub.description && sub.description.includes(sku)
      );
      if (isQuantity) {
        upgradeOptionalCancel.value.push({
          id: sub.id,
          description: sub.description,
          value: sub.value,
        });
      }
    }
  });

  if (
    upgradeAutoCancel.value.length > 0 ||
    upgradeOptionalCancel.value.length > 0
  ) {
    showUpgradeModal.value = true;
  } else {
    showUpgradeModal.value = true;
  }
};

const confirmUpgrade = async () => {
  isSubscribing.value.upgrade = true;
  try {
    const toCancel = [
      ...upgradeAutoCancel.value.map(s => s.id),
      ...selectedOptionalCancel.value,
    ];
    await FinanceAPI.changePlan(upgradePlanData.value.id, toCancel);
    useAlert(t('BILLING.PAGE.STORE.PLAN_UPDATED'));
    setTimeout(() => window.location.reload(), 1500);
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isSubscribing.value.upgrade = false;
    showUpgradeModal.value = false;
  }
};

const updatePlan = planId => {
  const plan = availablePlans.value.find(p => p.id === planId);
  if (plan) openUpgradeModal(plan);
};

const cancelSubscription = async subscriptionId => {
  if (!confirm('Tem certeza que deseja cancelar esta assinatura?')) return;
  try {
    await FinanceAPI.cancelSubscription(subscriptionId);
    window.bus.$emit('newToastMessage', 'Assinatura cancelada com sucesso!');
    fetchFinanceData();
  } catch (err) {
    window.bus.$emit('newToastMessage', 'Erro ao cancelar assinatura.');
  }
};

onMounted(() => {
  fetchFinanceData();
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING.PAGE.TITLE')"
        :description="$t('BILLING.PAGE.DESCRIPTION')"
        feature-name="finance"
      />
    </template>
    <template #body>
      <div
        class="flex w-full max-w-6xl flex-col gap-6 mx-auto px-4 pb-12 sm:px-0"
      >
        <!-- Tabs -->
        <div
          class="flex border-b border-slate-200 dark:border-slate-800 mb-2 overflow-x-auto"
        >
          <button
            :class="{
              'border-blue-600 text-blue-600 font-medium':
                currentTab === 'plan',
              'border-transparent text-slate-500 hover:text-slate-700':
                currentTab !== 'plan',
            }"
            class="px-4 py-3 border-b-2 text-sm transition-colors whitespace-nowrap"
            @click="currentTab = 'plan'"
          >
            {{ $t('BILLING.PAGE.TABS.PLAN') }}
          </button>
          <button
            :class="{
              'border-blue-600 text-blue-600 font-medium':
                currentTab === 'store',
              'border-transparent text-slate-500 hover:text-slate-700':
                currentTab !== 'store',
            }"
            class="px-4 py-3 border-b-2 text-sm transition-colors whitespace-nowrap"
            @click="currentTab = 'store'"
          >
            {{ $t('BILLING.PAGE.TABS.STORE') }}
          </button>
          <button
            :class="{
              'border-blue-600 text-blue-600 font-medium':
                currentTab === 'invoices',
              'border-transparent text-slate-500 hover:text-slate-700':
                currentTab !== 'invoices',
            }"
            class="px-4 py-3 border-b-2 text-sm transition-colors whitespace-nowrap"
            @click="currentTab = 'invoices'"
          >
            {{ $t('BILLING.PAGE.TABS.INVOICES') }}
          </button>
          <button
            :class="{
              'border-blue-600 text-blue-600 font-medium':
                currentTab === 'subscriptions',
              'border-transparent text-slate-500 hover:text-slate-700':
                currentTab !== 'subscriptions',
            }"
            class="px-4 py-3 border-b-2 text-sm transition-colors whitespace-nowrap"
            @click="currentTab = 'subscriptions'"
          >
            {{ $t('BILLING.PAGE.TABS.SUBSCRIPTIONS') }}
          </button>
          <button
            :class="{
              'border-blue-600 text-blue-600 font-medium':
                currentTab === 'preferences',
              'border-transparent text-slate-500 hover:text-slate-700':
                currentTab !== 'preferences',
            }"
            class="px-4 py-3 border-b-2 text-sm transition-colors whitespace-nowrap"
            @click="currentTab = 'preferences'"
          >
            {{ $t('BILLING.PAGE.TABS.PREFERENCES') }}
          </button>
        </div>

        <!-- PLAN TAB -->
        <div v-if="currentTab === 'plan'" class="space-y-6 max-w-4xl pt-4">
          <div
            class="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 sm:p-8 text-white shadow-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
          >
            <div>
              <h2
                class="text-sm font-semibold uppercase tracking-wider text-blue-100 mb-1"
              >
                {{ $t('BILLING.PAGE.CURRENT_PLAN') }}
              </h2>
              <div class="text-4xl font-bold mb-2">{{ planName }}</div>
              <div class="flex flex-col sm:flex-row gap-4 mt-4">
                <div class="bg-white/10 px-4 py-2 rounded-lg">
                  <span
                    class="block text-xs text-blue-200 uppercase tracking-wider mb-1"
                    >{{ $t('BILLING.PAGE.PLAN_VALUE') }}</span
                  >
                  <span class="font-semibold text-xl"
                    >R$
                    {{
                      Number(planPrice || 0)
                        .toFixed(2)
                        .replace('.', ',')
                    }}<span class="text-sm text-blue-200 font-normal"
                      >{{ $t('BILLING.PAGE.MONTH') }}</span
                    ></span
                  >
                </div>
                <div
                  v-if="Number(extrasPrice || 0) > 0"
                  class="bg-white/10 px-4 py-2 rounded-lg"
                >
                  <span
                    class="block text-xs text-blue-200 uppercase tracking-wider mb-1"
                    >{{ $t('BILLING.PAGE.EXTRA_SERVICES') }}</span
                  >
                  <span class="font-semibold text-xl"
                    >+ R$
                    {{
                      Number(extrasPrice || 0)
                        .toFixed(2)
                        .replace('.', ',')
                    }}<span class="text-sm text-blue-200 font-normal"
                      >{{ $t('BILLING.PAGE.MONTH') }}</span
                    ></span
                  >
                </div>
              </div>
            </div>
            <button
              class="w-full sm:w-auto justify-center bg-white text-blue-600 font-semibold px-6 py-3 rounded-xl hover:bg-blue-50 transition-colors shadow flex items-center gap-2 whitespace-nowrap"
              @click="showPlanUpgradeModal = true"
            >
              <span class="i-lucide-arrow-up-circle" />
              {{ $t('BILLING.PAGE.UPGRADE') }}
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm"
            >
              <h3
                class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2"
              >
                <span class="i-lucide-bar-chart-2 text-blue-500" />
                {{ $t('BILLING.PAGE.ACCOUNT_LIMITS') }}
              </h3>
              <ul class="space-y-3">
                <li
                  class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800"
                >
                  <span class="text-slate-600 dark:text-slate-400"
                    >{{ $t('BILLING.PAGE.USERS_AGENTS') }}</span
                  >
                  <span class="font-medium text-slate-900 dark:text-slate-100">
                    {{ limitsUsage.users?.usage || 0 }} /
                    {{
                      limitsUsage.users?.max != null
                        ? limitsUsage.users.max
                        : $t('BILLING.PAGE.UNLIMITED')
                    }}
                    <span
                      v-if="limitsUsage.users?.extra_max > 0"
                      class="text-xs text-blue-500 font-bold ml-1"
                      >(+{{ limitsUsage.users.extra_max }}
                      {{ $t('BILLING.PAGE.EXTRA') }})</span
                    >
                  </span>
                </li>
                <li
                  class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800"
                >
                  <span class="text-slate-600 dark:text-slate-400"
                    >{{ $t('BILLING.PAGE.ACTIVE_CAMPAIGNS') }}</span
                  >
                  <span class="font-medium text-slate-900 dark:text-slate-100">
                    {{ limitsUsage.campaigns?.usage || 0 }} /
                    {{
                      limitsUsage.campaigns?.max != null
                        ? limitsUsage.campaigns.max
                        : $t('BILLING.PAGE.UNLIMITED')
                    }}
                    <span
                      v-if="limitsUsage.campaigns?.extra_max > 0"
                      class="text-xs text-blue-500 font-bold ml-1"
                      >(+{{ limitsUsage.campaigns.extra_max }}
                      {{ $t('BILLING.PAGE.EXTRA') }})</span
                    >
                  </span>
                </li>
                <!-- Canais Específicos -->
                <li
                  class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800"
                >
                  <span
                    class="text-slate-600 dark:text-slate-400 flex items-center gap-2"
                    ><span class="i-lucide-message-circle text-emerald-500" />
                    WhatsApp</span
                  >
                  <span class="font-medium text-slate-900 dark:text-slate-100"
                    >{{ limitsUsage.whatsapp?.usage || 0 }} /
                    {{
                      limitsUsage.whatsapp?.max != null
                        ? limitsUsage.whatsapp.max
                        : 'Ilimitado'
                    }}
                    <span
                      v-if="limitsUsage.whatsapp?.extra_max > 0"
                      class="text-xs text-blue-500 font-bold ml-1"
                      >(+{{ limitsUsage.whatsapp.extra_max }} extra)</span
                    ></span
                  >
                </li>
                <li
                  class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800"
                >
                  <span
                    class="text-slate-600 dark:text-slate-400 flex items-center gap-2"
                    ><span class="i-lucide-camera text-rose-500" />
                    Instagram</span
                  >
                  <span class="font-medium text-slate-900 dark:text-slate-100"
                    >{{ limitsUsage.instagram?.usage || 0 }} /
                    {{
                      limitsUsage.instagram?.max != null
                        ? limitsUsage.instagram.max
                        : 'Ilimitado'
                    }}
                    <span
                      v-if="limitsUsage.instagram?.extra_max > 0"
                      class="text-xs text-blue-500 font-bold ml-1"
                      >(+{{ limitsUsage.instagram.extra_max }} extra)</span
                    ></span
                  >
                </li>
                <li
                  class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800"
                >
                  <span
                    class="text-slate-600 dark:text-slate-400 flex items-center gap-2"
                    ><span class="i-lucide-facebook text-blue-600" />
                    Facebook</span
                  >
                  <span class="font-medium text-slate-900 dark:text-slate-100"
                    >{{ limitsUsage.facebook?.usage || 0 }} /
                    {{
                      limitsUsage.facebook?.max != null
                        ? limitsUsage.facebook.max
                        : 'Ilimitado'
                    }}
                    <span
                      v-if="limitsUsage.facebook?.extra_max > 0"
                      class="text-xs text-blue-500 font-bold ml-1"
                      >(+{{ limitsUsage.facebook.extra_max }} extra)</span
                    ></span
                  >
                </li>
                <li
                  class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800"
                >
                  <span
                    class="text-slate-600 dark:text-slate-400 flex items-center gap-2"
                    ><span class="i-lucide-send text-sky-500" /> Telegram</span
                  >
                  <span class="font-medium text-slate-900 dark:text-slate-100"
                    >{{ limitsUsage.telegram?.usage || 0 }} /
                    {{
                      limitsUsage.telegram?.max != null
                        ? limitsUsage.telegram.max
                        : 'Ilimitado'
                    }}
                    <span
                      v-if="limitsUsage.telegram?.extra_max > 0"
                      class="text-xs text-blue-500 font-bold ml-1"
                      >(+{{ limitsUsage.telegram.extra_max }} extra)</span
                    ></span
                  >
                </li>
                <li
                  class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800"
                >
                  <span
                    class="text-slate-600 dark:text-slate-400 flex items-center gap-2"
                    ><span class="i-lucide-layout-template text-purple-500" />
                    Web Widget</span
                  >
                  <span class="font-medium text-slate-900 dark:text-slate-100"
                    >{{ limitsUsage.web_widget?.usage || 0 }} /
                    {{
                      limitsUsage.web_widget?.max != null
                        ? limitsUsage.web_widget.max
                        : 'Ilimitado'
                    }}
                    <span
                      v-if="limitsUsage.web_widget?.extra_max > 0"
                      class="text-xs text-blue-500 font-bold ml-1"
                      >(+{{ limitsUsage.web_widget.extra_max }} extra)</span
                    ></span
                  >
                </li>
                <li
                  class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800"
                >
                  <span
                    class="text-slate-600 dark:text-slate-400 flex items-center gap-2"
                    ><span class="i-lucide-mail text-amber-500" /> Email</span
                  >
                  <span class="font-medium text-slate-900 dark:text-slate-100"
                    >{{ limitsUsage.email?.usage || 0 }} /
                    {{
                      limitsUsage.email?.max != null
                        ? limitsUsage.email.max
                        : 'Ilimitado'
                    }}
                    <span
                      v-if="limitsUsage.email?.extra_max > 0"
                      class="text-xs text-blue-500 font-bold ml-1"
                      >(+{{ limitsUsage.email.extra_max }} extra)</span
                    ></span
                  >
                </li>
                <li
                  class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800"
                >
                  <span
                    class="text-slate-600 dark:text-slate-400 flex items-center gap-2"
                    ><span class="i-lucide-code text-slate-500" /> API /
                    Custom</span
                  >
                  <span class="font-medium text-slate-900 dark:text-slate-100"
                    >{{ limitsUsage.api?.usage || 0 }} /
                    {{
                      limitsUsage.api?.max != null
                        ? limitsUsage.api.max
                        : 'Ilimitado'
                    }}
                    <span
                      v-if="limitsUsage.api?.extra_max > 0"
                      class="text-xs text-blue-500 font-bold ml-1"
                      >(+{{ limitsUsage.api.extra_max }} extra)</span
                    ></span
                  >
                </li>
                <li
                  class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800"
                >
                  <span
                    class="text-slate-600 dark:text-slate-400 flex items-center gap-2"
                    ><span class="i-lucide-message-square text-emerald-500" />
                    Saldo de SMS</span
                  >
                  <span class="font-medium text-slate-900 dark:text-slate-100"
                    >{{ limitsUsage.sms?.max || 0 }} envios</span
                  >
                </li>
              </ul>
            </div>

            <div
              class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm"
            >
              <h3
                class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2"
              >
                <span class="i-lucide-check-circle text-emerald-500" />
                {{ $t('BILLING.PAGE.ACTIVE_MODULES') }}
                Ativos
              </h3>
              <ul class="space-y-3">
                <li
                  v-for="module in storeModules.filter(m =>
                    [
                      'api',
                      'automations',
                      'reports',
                      'tickets',
                      'crm',
                      'campaigns',
                      'sla',
                      'ia',
                      'alerts',
                      'white_label',
                      'sms',
                      'sgp_integration',
                      'ixc_integration',
                      'mkauth_integration',
                      'hubsoft_integration',
                    ].includes(m.sku)
                  )"
                  :key="'active_' + module.sku"
                  class="flex items-center gap-3 py-1"
                >
                  <span
                    :class="
                      module.is_active
                        ? 'i-lucide-check text-emerald-500'
                        : 'i-lucide-x text-rose-500'
                    "
                    class="text-lg"
                  />
                  <span
                    :class="
                      module.is_active
                        ? 'text-slate-800 dark:text-slate-200'
                        : 'text-slate-400 line-through'
                    "
                    class="capitalize"
                  >
                    {{ module.name }}
                  </span>
                </li>
              </ul>
            </div>

            <!-- Card da Inteligência Artificial -->
            <div
              class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm md:col-span-2"
            >
              <h3
                class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2"
              >
                <span class="i-lucide-cpu text-indigo-500" />
                {{ $t('BILLING.PAGE.AI_USAGE.TITLE') }}
              </h3>
              <p class="text-xs text-slate-500 dark:text-slate-400 -mt-2 mb-6">
                {{
                  $t('BILLING.PAGE.AI_USAGE.RESET_INFO', {
                    date: aiUsageResetLabel,
                  })
                }}
              </p>

              <div class="mb-2 flex justify-between items-end">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >{{ $t('BILLING.PAGE.AI_USAGE.ANALYTICS_REPORTS') }}</span
                >
                <span
                  class="text-sm font-bold text-slate-900 dark:text-slate-100"
                >
                  {{ limitsUsage.lumerai_analytics_reports?.usage || 0 }} /
                  {{ limitsUsage.lumerai_analytics_reports?.max || 0 }}
                  {{ $t('BILLING.PAGE.AI_USAGE.REPORTS') }}
                </span>
              </div>
              <div
                class="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-3 mb-2 overflow-hidden relative"
              >
                <div
                  class="h-3 rounded-full transition-all duration-500 absolute left-0 top-0"
                  :style="{
                    width:
                      usagePercentage(limitsUsage.lumerai_analytics_reports) +
                      '%',
                    backgroundColor: usageBarColor(
                      limitsUsage.lumerai_analytics_reports
                    ),
                  }"
                />
              </div>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-2 mb-6">
                {{ $t('BILLING.PAGE.AI_USAGE.ANALYTICS_INFO') }}
              </p>

              <div class="mb-2 flex justify-between items-end">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >{{ $t('BILLING.PAGE.AI_USAGE.AUTOPILOT_RESPONSES') }}</span
                >
                <span
                  class="text-sm font-bold text-slate-900 dark:text-slate-100"
                >
                  {{ limitsUsage.lumerai_responses?.usage || 0 }} /
                  {{ limitsUsage.lumerai_responses?.max || 0 }}
                  {{ $t('BILLING.PAGE.AI_USAGE.RESPONSES') }}
                </span>
              </div>
              <div
                class="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-3 mb-2 overflow-hidden relative"
              >
                <div
                  class="h-3 rounded-full transition-all duration-500 absolute left-0 top-0"
                  :style="{
                    width: usagePercentage(limitsUsage.lumerai_responses) + '%',
                    backgroundColor: usageBarColor(limitsUsage.lumerai_responses),
                  }"
                />
              </div>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-2 mb-6">
                {{ $t('BILLING.PAGE.AI_USAGE.AUTOPILOT_INFO') }}
              </p>

              <div class="mb-2 flex justify-between items-end">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >{{ $t('BILLING.PAGE.AI_USAGE.KNOWLEDGE_PAGES') }}</span
                >
                <span
                  class="text-sm font-bold text-slate-900 dark:text-slate-100"
                >
                  {{ limitsUsage.lumerai_documents?.usage || 0 }} /
                  {{ limitsUsage.lumerai_documents?.max || 0 }}
                  {{ $t('BILLING.PAGE.AI_USAGE.PAGES') }}
                </span>
              </div>
              <div
                class="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-3 mb-2 overflow-hidden relative"
              >
                <div
                  class="h-3 rounded-full transition-all duration-500 absolute left-0 top-0"
                  :style="{
                    width: usagePercentage(limitsUsage.lumerai_documents) + '%',
                    backgroundColor: usageBarColor(limitsUsage.lumerai_documents),
                  }"
                />
              </div>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-2 mb-6">
                {{ $t('BILLING.PAGE.AI_USAGE.KNOWLEDGE_INFO') }}
              </p>

              <div class="mb-2 flex justify-between items-end">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >{{ $t('BILLING.PAGE.AI_USAGE.CONVERSATION_SUMMARIES') }}</span
                >
                <span
                  class="text-sm font-bold text-slate-900 dark:text-slate-100"
                >
                  {{ limitsUsage.lumerai_summaries?.usage || 0 }} /
                  {{ limitsUsage.lumerai_summaries?.max || 0 }}
                  {{ $t('BILLING.PAGE.AI_USAGE.SUMMARIES') }}
                </span>
              </div>
              <div
                class="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-3 mb-2 overflow-hidden relative"
              >
                <div
                  class="h-3 rounded-full transition-all duration-500 absolute left-0 top-0"
                  :style="{
                    width: usagePercentage(limitsUsage.lumerai_summaries) + '%',
                    backgroundColor: usageBarColor(limitsUsage.lumerai_summaries),
                  }"
                />
              </div>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-2 mb-6">
                {{ $t('BILLING.PAGE.AI_USAGE.CONVERSATION_INFO') }}
              </p>

              <div class="mb-2 flex justify-between items-end">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >{{ $t('BILLING.PAGE.AI_USAGE.TICKET_SUMMARIES') }}</span
                >
                <span
                  class="text-sm font-bold text-slate-900 dark:text-slate-100"
                >
                  {{ limitsUsage.ticket_summaries?.usage || 0 }} /
                  {{ limitsUsage.ticket_summaries?.max || 0 }}
                  {{ $t('BILLING.PAGE.AI_USAGE.SUMMARIES') }}
                </span>
              </div>
              <div
                class="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-3 mb-2 overflow-hidden relative"
              >
                <div
                  class="h-3 rounded-full transition-all duration-500 absolute left-0 top-0"
                  :style="{
                    width: usagePercentage(limitsUsage.ticket_summaries) + '%',
                    backgroundColor: usageBarColor(limitsUsage.ticket_summaries),
                  }"
                />
              </div>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-2 mb-6">
                {{ $t('BILLING.PAGE.AI_USAGE.TICKET_INFO') }}
              </p>

              <div class="mb-2 flex justify-between items-end">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >{{ $t('BILLING.PAGE.AI_USAGE.MINI_AI') }}</span
                >
                <span
                  class="text-sm font-bold text-slate-900 dark:text-slate-100"
                >
                  {{ limitsUsage.mini_ai?.usage ?? 0 }} /
                  {{ limitsUsage.mini_ai?.max ?? 1000 }}
                  {{ $t('BILLING.PAGE.AI_USAGE.REQUESTS') }}
                </span>
              </div>

              <div
                class="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-3 mb-2 overflow-hidden relative"
              >
                <div
                  class="h-3 rounded-full transition-all duration-500 absolute left-0 top-0"
                  :style="{
                    width: usagePercentage(limitsUsage.mini_ai) + '%',
                    backgroundColor: usageBarColor(limitsUsage.mini_ai),
                  }"
                />
              </div>

              <p class="text-xs text-slate-500 dark:text-slate-400 mt-2 mb-6">
                <span
                  v-if="
                    (limitsUsage.mini_ai?.usage ?? 0) >=
                    (limitsUsage.mini_ai?.max ?? 1000)
                  "
                  class="text-rose-500 font-medium"
                  >{{ $t('BILLING.PAGE.AI_USAGE.MINI_AI_EXCEEDED') }}</span
                >
                <span v-else
                  >{{ $t('BILLING.PAGE.AI_USAGE.MINI_AI_INFO') }}</span
                >
              </p>

              <div class="mb-2 flex justify-between items-end">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >{{ $t('BILLING.PAGE.AI_USAGE.AUDIO_TRANSCRIPTIONS') }}</span
                >
                <span
                  class="text-sm font-bold text-slate-900 dark:text-slate-100"
                >
                  {{ limitsUsage.audio_transcriptions?.usage || 0 }} /
                  {{ limitsUsage.audio_transcriptions?.max ?? 100 }}
                  {{ $t('BILLING.PAGE.AI_USAGE.REQUESTS') }}
                </span>
              </div>

              <div
                class="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-3 mb-2 overflow-hidden relative"
              >
                <div
                  class="h-3 rounded-full transition-all duration-500 absolute left-0 top-0"
                  :style="{
                    width:
                      usagePercentage(limitsUsage.audio_transcriptions) + '%',
                    backgroundColor: usageBarColor(
                      limitsUsage.audio_transcriptions
                    ),
                  }"
                />
              </div>

              <p class="text-xs text-slate-500 dark:text-slate-400 mt-2">
                <span
                  v-if="
                    (limitsUsage.audio_transcriptions?.usage || 0) >=
                    (limitsUsage.audio_transcriptions?.max ?? 100)
                  "
                  class="text-rose-500 font-medium"
                  >{{ $t('BILLING.PAGE.AI_USAGE.AUDIO_EXCEEDED') }}</span
                >
                <span v-else
                  >{{ $t('BILLING.PAGE.AI_USAGE.AUDIO_INFO') }}</span
                >
              </p>
            </div>
          </div>
        </div>

        <!-- STORE TAB -->
        <div v-if="currentTab === 'store'" class="space-y-8 pt-4">
          <!-- Canais de Atendimento -->
          <section>
            <h2
              class="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2"
            >
              <span class="i-lucide-message-circle text-blue-500" />
              {{ $t('BILLING.PAGE.STORE.CHANNELS_TITLE') }}
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <!-- WhatsApp -->
              <div
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between group"
              >
                <div>
                  <div
                    class="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center mb-4 text-emerald-600 dark:text-emerald-400"
                  >
                    <span class="i-lucide-message-circle text-2xl" />
                  </div>
                  <h3 class="text-lg font-bold text-slate-800 dark:text-white">
                    {{ $t('BILLING.PAGE.STORE.WHATSAPP_TITLE') }}
                  </h3>
                  <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    {{ $t('BILLING.PAGE.STORE.WHATSAPP_DESCRIPTION') }}
                  </p>
                  <p
                    class="text-2xl font-black mt-4 text-slate-800 dark:text-white"
                  >
                    R$ {{ prices.inbox_whatsapp
                    }}<span class="text-sm font-medium text-slate-400"
                      >{{ $t('BILLING.PAGE.STORE.MONTH') }}</span
                    >
                  </p>
                </div>
                <div class="mt-6">
                  <div
                    class="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-lg p-1 border border-slate-200 dark:border-slate-700 mb-3"
                  >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md transition-colors"
                      @click="adjustQuantity('inbox_whatsapp', -1)"
                    >
                      -
                    </button>
                    <span
                      class="font-medium text-slate-700 dark:text-slate-300"
                      >{{ quantities.inbox_whatsapp }}</span
                    >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md transition-colors"
                      @click="adjustQuantity('inbox_whatsapp', 1)"
                    >
                      +
                    </button>
                  </div>
                  <ButtonV4
                    class="w-full justify-center transition-transform active:scale-95"
                    color="teal"
                    :is-loading="isSubscribing['inbox_whatsapp']"
                    @click="handleSubscribe('inbox_whatsapp')"
                  >
                    {{
                      $t('BILLING.PAGE.STORE.CHANNEL_SUBSCRIBE', {
                        channel: 'WhatsApp',
                      })
                    }}
                  </ButtonV4>
                </div>
              </div>

              <!-- Instagram -->
              <div
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between group"
              >
                <div>
                  <div
                    class="w-12 h-12 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500 rounded-lg flex items-center justify-center mb-4 text-white"
                  >
                    <span class="i-lucide-camera text-2xl" />
                  </div>
                  <h3 class="text-lg font-bold text-slate-800 dark:text-white">
                    {{ $t('BILLING.PAGE.STORE.INSTAGRAM_TITLE') }}
                  </h3>
                  <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    {{ $t('BILLING.PAGE.STORE.INSTAGRAM_DESCRIPTION') }}
                  </p>
                  <p
                    class="text-2xl font-black mt-4 text-slate-800 dark:text-white"
                  >
                    R$ {{ prices.inbox_instagram
                    }}<span class="text-sm font-medium text-slate-400"
                      >{{ $t('BILLING.PAGE.STORE.MONTH') }}</span
                    >
                  </p>
                </div>
                <div class="mt-6">
                  <div
                    class="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-lg p-1 border border-slate-200 dark:border-slate-700 mb-3"
                  >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md transition-colors"
                      @click="adjustQuantity('inbox_instagram', -1)"
                    >
                      -
                    </button>
                    <span
                      class="font-medium text-slate-700 dark:text-slate-300"
                      >{{ quantities.inbox_instagram }}</span
                    >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md transition-colors"
                      @click="adjustQuantity('inbox_instagram', 1)"
                    >
                      +
                    </button>
                  </div>
                  <ButtonV4
                    class="w-full justify-center transition-transform active:scale-95"
                    color="blue"
                    :is-loading="isSubscribing['inbox_instagram']"
                    @click="handleSubscribe('inbox_instagram')"
                  >
                    {{
                      $t('BILLING.PAGE.STORE.CHANNEL_SUBSCRIBE', {
                        channel: 'Instagram',
                      })
                    }}
                  </ButtonV4>
                </div>
              </div>

              <!-- Facebook -->
              <div
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between group"
              >
                <div>
                  <div
                    class="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-4 text-blue-600"
                  >
                    <span class="i-lucide-facebook text-2xl" />
                  </div>
                  <h3 class="text-lg font-bold text-slate-800 dark:text-white">
                    {{ $t('BILLING.PAGE.STORE.FACEBOOK_TITLE') }}
                  </h3>
                  <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    {{ $t('BILLING.PAGE.STORE.FACEBOOK_DESCRIPTION') }}
                  </p>
                  <p
                    class="text-2xl font-black mt-4 text-slate-800 dark:text-white"
                  >
                    R$ {{ prices.inbox_facebook
                    }}<span class="text-sm font-medium text-slate-400"
                      >{{ $t('BILLING.PAGE.STORE.MONTH') }}</span
                    >
                  </p>
                </div>
                <div class="mt-6">
                  <div
                    class="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-lg p-1 border border-slate-200 dark:border-slate-700 mb-3"
                  >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md transition-colors"
                      @click="adjustQuantity('inbox_facebook', -1)"
                    >
                      -
                    </button>
                    <span
                      class="font-medium text-slate-700 dark:text-slate-300"
                      >{{ quantities.inbox_facebook }}</span
                    >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md transition-colors"
                      @click="adjustQuantity('inbox_facebook', 1)"
                    >
                      +
                    </button>
                  </div>
                  <ButtonV4
                    class="w-full justify-center transition-transform active:scale-95"
                    color="blue"
                    :is-loading="isSubscribing['inbox_facebook']"
                    @click="handleSubscribe('inbox_facebook')"
                  >
                    {{
                      $t('BILLING.PAGE.STORE.CHANNEL_SUBSCRIBE', {
                        channel: 'Facebook',
                      })
                    }}
                  </ButtonV4>
                </div>
              </div>

              <!-- Telegram -->
              <div
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between group"
              >
                <div>
                  <div
                    class="w-12 h-12 bg-sky-100 dark:bg-sky-900/30 rounded-lg flex items-center justify-center mb-4 text-sky-500"
                  >
                    <span class="i-lucide-send text-2xl" />
                  </div>
                  <h3 class="text-lg font-bold text-slate-800 dark:text-white">
                    {{ $t('BILLING.PAGE.STORE.TELEGRAM_TITLE') }}
                  </h3>
                  <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    {{ $t('BILLING.PAGE.STORE.TELEGRAM_DESCRIPTION') }}
                  </p>
                  <p
                    class="text-2xl font-black mt-4 text-slate-800 dark:text-white"
                  >
                    R$ {{ prices.inbox_telegram
                    }}<span class="text-sm font-medium text-slate-400"
                      >{{ $t('BILLING.PAGE.STORE.MONTH') }}</span
                    >
                  </p>
                </div>
                <div class="mt-6">
                  <div
                    class="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-lg p-1 border border-slate-200 dark:border-slate-700 mb-3"
                  >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md transition-colors"
                      @click="adjustQuantity('inbox_telegram', -1)"
                    >
                      -
                    </button>
                    <span
                      class="font-medium text-slate-700 dark:text-slate-300"
                      >{{ quantities.inbox_telegram }}</span
                    >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md transition-colors"
                      @click="adjustQuantity('inbox_telegram', 1)"
                    >
                      +
                    </button>
                  </div>
                  <ButtonV4
                    class="w-full justify-center transition-transform active:scale-95"
                    color="blue"
                    :is-loading="isSubscribing['inbox_telegram']"
                    @click="handleSubscribe('inbox_telegram')"
                  >
                    {{
                      $t('BILLING.PAGE.STORE.CHANNEL_SUBSCRIBE', {
                        channel: 'Telegram',
                      })
                    }}
                  </ButtonV4>
                </div>
              </div>
            </div>
          </section>

          <!-- Pacotes de SMS -->
          <section>
            <h2
              class="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2 mt-8"
            >
              <span class="i-lucide-smartphone text-emerald-500" />
              {{ $t('BILLING.PAGE.STORE.SMS_TITLE') }}
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <!-- SMS 1000 -->
              <div
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
              >
                <div>
                  <div
                    class="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center mb-4 text-emerald-600"
                  >
                    <span class="i-lucide-message-square text-2xl" />
                  </div>
                  <h3 class="text-lg font-bold text-slate-800 dark:text-white">
                    {{ $t('BILLING.PAGE.STORE.SMS_1000_TITLE') }}
                  </h3>
                  <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    {{ $t('BILLING.PAGE.STORE.SMS_1000_DESCRIPTION') }}
                  </p>
                  <p
                    class="text-2xl font-black mt-4 text-slate-800 dark:text-white"
                  >
                    R$ {{ prices.sms_pack_1000
                    }}<span class="text-sm font-medium text-slate-400"
                      >{{ $t('BILLING.PAGE.STORE.MONTH_OR_ONE_TIME') }}</span
                    >
                  </p>
                </div>
                <div class="mt-6">
                  <div class="flex flex-col gap-2">
                    <ButtonV4
                      class="w-full justify-center"
                      color="blue"
                      :is-loading="isSubscribing['sms_pack_1000']"
                      @click="handleSubscribe('sms_pack_1000')"
                    >
                      {{ $t('BILLING.PAGE.STORE.MONTHLY') }}
                    </ButtonV4>
                    <ButtonV4
                      class="w-full justify-center"
                      variant="outline"
                      @click="handleSubscribe('sms_pack_1000')"
                    >
                      {{ $t('BILLING.PAGE.STORE.ONE_TIME') }}
                    </ButtonV4>
                  </div>
                </div>
              </div>

              <!-- SMS 5000 -->
              <div
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
              >
                <div>
                  <div
                    class="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center mb-4 text-emerald-600"
                  >
                    <span class="i-lucide-message-square text-2xl" />
                  </div>
                  <h3 class="text-lg font-bold text-slate-800 dark:text-white">
                    {{ $t('BILLING.PAGE.STORE.SMS_5000_TITLE') }}
                  </h3>
                  <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    {{ $t('BILLING.PAGE.STORE.SMS_5000_DESCRIPTION') }}
                  </p>
                  <p
                    class="text-2xl font-black mt-4 text-slate-800 dark:text-white"
                  >
                    R$ {{ prices.sms_pack_5000
                    }}<span class="text-sm font-medium text-slate-400"
                      >{{ $t('BILLING.PAGE.STORE.MONTH_OR_ONE_TIME') }}</span
                    >
                  </p>
                </div>
                <div class="mt-6">
                  <div class="flex flex-col gap-2">
                    <ButtonV4
                      class="w-full justify-center"
                      color="blue"
                      :is-loading="isSubscribing['sms_pack_5000']"
                      @click="handleSubscribe('sms_pack_5000')"
                    >
                      {{ $t('BILLING.PAGE.STORE.MONTHLY') }}
                    </ButtonV4>
                    <ButtonV4
                      class="w-full justify-center"
                      variant="outline"
                      @click="handleSubscribe('sms_pack_5000')"
                    >
                      {{ $t('BILLING.PAGE.STORE.ONE_TIME') }}
                    </ButtonV4>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Produtividade & Equipe -->
          <section>
            <h2
              class="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2"
            >
              <span class="i-lucide-users text-purple-500" />
              {{ $t('BILLING.PAGE.STORE.TEAM_TITLE') }}
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <!-- Agent Extra -->
              <div
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
              >
                <div>
                  <div
                    class="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mb-4 text-purple-600"
                  >
                    <span class="i-lucide-user-plus text-2xl" />
                  </div>
                  <h3 class="text-lg font-bold text-slate-800 dark:text-white">
                    {{ $t('BILLING.PAGE.STORE.AGENT_TITLE') }}
                  </h3>
                  <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    {{ $t('BILLING.PAGE.STORE.AGENT_DESCRIPTION') }}
                  </p>
                  <p
                    class="text-2xl font-black mt-4 text-slate-800 dark:text-white"
                  >
                    R$ {{ prices.agent_extra
                    }}<span class="text-sm font-medium text-slate-400"
                      >{{ $t('BILLING.PAGE.STORE.MONTH') }}</span
                    >
                  </p>
                </div>
                <div class="mt-6">
                  <div
                    class="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-lg p-1 border border-slate-200 dark:border-slate-700 mb-3"
                  >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
                      @click="adjustQuantity('agent_extra', -1)"
                    >
                      -
                    </button>
                    <span
                      class="font-medium text-slate-700 dark:text-slate-300"
                      >{{ quantities.agent_extra }}</span
                    >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
                      @click="adjustQuantity('agent_extra', 1)"
                    >
                      +
                    </button>
                  </div>
                  <ButtonV4
                    class="w-full justify-center transition-transform active:scale-95"
                    color="amber"
                    :is-loading="isSubscribing['agent_extra']"
                    @click="handleSubscribe('agent_extra')"
                  >
                    {{ $t('BILLING.PAGE.STORE.AGENT_SUBSCRIBE') }}
                  </ButtonV4>
                </div>
              </div>

              <!-- Trigger Words -->
              <div
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
              >
                <div>
                  <div
                    class="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mb-4 text-orange-600"
                  >
                    <span class="i-lucide-zap text-2xl" />
                  </div>
                  <h3 class="text-lg font-bold text-slate-800 dark:text-white">
                    {{ $t('BILLING.PAGE.STORE.TRIGGER_TITLE') }}
                  </h3>
                  <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    {{ $t('BILLING.PAGE.STORE.TRIGGER_DESCRIPTION') }}
                  </p>
                  <p
                    class="text-2xl font-black mt-4 text-slate-800 dark:text-white"
                  >
                    R$ {{ prices.trigger_words_pack
                    }}<span class="text-sm font-medium text-slate-400"
                      >{{ $t('BILLING.PAGE.STORE.MONTH') }}</span
                    >
                  </p>

                  <div
                    class="mt-3 bg-slate-50 dark:bg-slate-800 rounded p-2 text-xs text-slate-500 flex justify-between items-center border border-slate-100 dark:border-slate-700"
                  >
                    <span>{{ $t('BILLING.PAGE.STORE.CURRENT_USAGE') }}</span>
                    <span class="font-bold text-slate-700 dark:text-slate-300"
                      >3/3 {{ $t('BILLING.PAGE.STORE.CONTRACTED') }}</span
                    >
                  </div>
                </div>
                <div class="mt-6">
                  <div
                    class="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-lg p-1 border border-slate-200 dark:border-slate-700 mb-3"
                  >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
                      @click="adjustQuantity('trigger_words_pack', -1)"
                    >
                      -
                    </button>
                    <span class="font-medium text-slate-700 dark:text-slate-300"
                      >{{ quantities.trigger_words_pack }}
                      {{ $t('BILLING.PAGE.STORE.PACKAGES') }}</span
                    >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
                      @click="adjustQuantity('trigger_words_pack', 1)"
                    >
                      +
                    </button>
                  </div>
                  <ButtonV4
                    class="w-full justify-center transition-transform active:scale-95"
                    color="amber"
                    :is-loading="isSubscribing['trigger_words_pack']"
                    @click="handleSubscribe('trigger_words_pack')"
                  >
                    {{ $t('BILLING.PAGE.STORE.BUY_PACKAGE') }}
                  </ButtonV4>
                </div>
              </div>
            </div>
          </section>

          <!-- Inteligência Artificial -->
          <section>
            <h2
              class="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2"
            >
              <span class="i-lucide-bot text-indigo-500" />
              {{ $t('BILLING.PAGE.STORE.AI_STORE_TITLE') }}
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Mini AI Workflow -->
              <div
                class="bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/30 dark:to-slate-900 border border-indigo-100 dark:border-indigo-900/50 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
              >
                <div>
                  <div
                    class="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg flex items-center justify-center mb-4 text-indigo-600 dark:text-indigo-400"
                  >
                    <span class="i-lucide-cpu text-2xl" />
                  </div>
                  <h3 class="text-lg font-bold text-slate-800 dark:text-white">
                    {{ $t('BILLING.PAGE.STORE.MINI_WORKFLOW_TITLE') }}
                  </h3>
                  <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    {{ $t('BILLING.PAGE.STORE.MINI_WORKFLOW_DESCRIPTION') }}
                  </p>
                  <p
                    class="text-2xl font-black mt-4 text-slate-800 dark:text-white"
                  >
                    R$ {{ prices.mini_ai_workflow
                    }}<span class="text-sm font-medium text-slate-400"
                      >{{ $t('BILLING.PAGE.STORE.MONTH') }}</span
                    >
                  </p>
                </div>
                <div class="mt-6">
                  <div
                    class="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg p-1 border border-slate-200 dark:border-slate-700 mb-3"
                  >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                      @click="adjustQuantity('mini_ai_workflow', -1)"
                    >
                      -
                    </button>
                    <span
                      class="font-medium text-slate-700 dark:text-slate-300"
                      >{{ quantities.mini_ai_workflow }}</span
                    >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                      @click="adjustQuantity('mini_ai_workflow', 1)"
                    >
                      +
                    </button>
                  </div>
                  <ButtonV4
                    class="w-full justify-center transition-transform active:scale-95 bg-indigo-600 hover:bg-indigo-700 text-white"
                    :is-loading="isSubscribing['mini_ai_workflow']"
                    @click="handleSubscribe('mini_ai_workflow')"
                  >
                    {{ $t('BILLING.PAGE.STORE.BUY_PROCESSING') }}
                  </ButtonV4>
                </div>
              </div>

              <!-- LumerAI Pro analytics reports -->
              <div
                class="bg-gradient-to-br from-fuchsia-50 to-white dark:from-fuchsia-950/30 dark:to-slate-900 border border-fuchsia-100 dark:border-fuchsia-900/50 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
              >
                <div>
                  <div
                    class="w-12 h-12 bg-fuchsia-100 dark:bg-fuchsia-900/50 rounded-lg flex items-center justify-center mb-4 text-fuchsia-600 dark:text-fuchsia-400"
                  >
                    <span class="i-lucide-chart-no-axes-combined text-2xl" />
                  </div>
                  <h3 class="text-lg font-bold text-slate-800 dark:text-white">
                    {{ $t('BILLING.PAGE.STORE.ANALYTICS_PACK_TITLE') }}
                  </h3>
                  <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    {{ $t('BILLING.PAGE.STORE.ANALYTICS_PACK_DESCRIPTION') }}
                  </p>
                  <p
                    class="text-2xl font-black mt-4 text-slate-800 dark:text-white"
                  >
                    R$ {{ prices.lumerai_analytics_reports_pack
                    }}<span class="text-sm font-medium text-slate-400"
                      >{{ $t('BILLING.PAGE.STORE.MONTH') }}</span
                    >
                  </p>
                </div>
                <div class="mt-6">
                  <div
                    class="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg p-1 border border-slate-200 dark:border-slate-700 mb-3"
                  >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                      @click="adjustQuantity('lumerai_analytics_reports_pack', -1)"
                    >
                      -
                    </button>
                    <span
                      class="font-medium text-slate-700 dark:text-slate-300"
                      >{{ quantities.lumerai_analytics_reports_pack }}
                      {{ $t('BILLING.PAGE.STORE.PACK') }}</span
                    >
                    <button
                      class="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                      @click="adjustQuantity('lumerai_analytics_reports_pack', 1)"
                    >
                      +
                    </button>
                  </div>
                  <ButtonV4
                    class="w-full justify-center transition-transform active:scale-95 bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                    :is-loading="isSubscribing['lumerai_analytics_reports_pack']"
                    @click="handleSubscribe('lumerai_analytics_reports_pack')"
                  >
                    {{ $t('BILLING.PAGE.STORE.SUBSCRIBE_REPORTS') }}
                  </ButtonV4>
                </div>
              </div>
            </div>
          </section>

          <!-- Recursos Avançados (Dinâmico) -->
          <section>
            <h2
              class="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2"
            >
              <span class="i-lucide-layers text-cyan-500" />
              {{ $t('BILLING.PAGE.STORE.ADVANCED_TITLE') }}
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div
                v-for="module in storeModules.filter(m =>
                  [
                    'ia',
                    'alerts',
                    'tickets',
                    'crm',
                    'reports',
                    'sla',
                    'api',
                    'campaigns',
                    'white_label',
                    'sms',
                    'sgp_integration',
                    'ixc_integration',
                    'mkauth_integration',
                    'hubsoft_integration',
                    'inbound_emails',
                    'channel_email',
                    'channel_facebook',
                    'help_center',
                    'macros',
                    'labels',
                    'custom_attributes',
                    'automations',
                    'canned_responses',
                    'integrations',
                    'voice_recorder',
                    'channel_website',
                    'auto_resolve_conversations',
                    'channel_instagram',
                    'channel_tiktok',
                    'advanced_assignment',
                    'audit_logs',
                    'custom_roles',
                    'custom_tools',
                    'custom_reply_email',
                    'email_continuity_on_api_channel',
                    'semi_ia',
                    'report_rollup',
                    'conversation_required_attributes',
                    'saml',
                    'ai_markdown_images',
                    'mini_ai_workflow',
                    'billing_automation',
                    'instagram_advanced_ui',
                    'facebook_advanced_ui',
                    'lumerai_summaries',
                  ].includes(m.sku)
                )"
                :key="module.sku"
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
              >
                <div>
                  <div
                    class="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center mb-4 text-slate-600 dark:text-slate-300"
                  >
                    <span class="i-lucide-box text-2xl" />
                  </div>
                  <h3
                    class="text-lg font-bold text-slate-800 dark:text-white capitalize"
                  >
                    {{ moduleName(module.sku) || module.name }}
                  </h3>
                  <p
                    class="text-sm text-slate-500 dark:text-slate-400 mt-2 min-h-[40px]"
                  >
                    {{
                      moduleDescription(module.sku) ||
                      $t('BILLING.PAGE.STORE.FALLBACK_DESCRIPTION')
                    }}
                  </p>
                  <p
                    class="text-2xl font-black mt-4 text-slate-800 dark:text-white"
                  >
                    {{ module.is_active ? $t('BILLING.PAGE.STORE.INCLUDED') : 'R$ ' + module.price }}
                    <span
                      v-if="!module.is_active"
                      class="text-sm font-medium text-slate-400"
                      >{{ $t('BILLING.PAGE.STORE.MONTH') }}</span
                    >
                  </p>
                </div>
                <div class="mt-6">
                  <ButtonV4
                    class="w-full justify-center transition-transform active:scale-95"
                    color="slate"
                    :disabled="
                      module.is_active ||
                      (isIspSku(module.sku) &&
                        activeIspIntegration &&
                        !module.is_active)
                    "
                    :class="{
                      'opacity-50 cursor-not-allowed':
                        module.is_active ||
                        (isIspSku(module.sku) &&
                          activeIspIntegration &&
                          !module.is_active),
                    }"
                    :is-loading="isSubscribing[module.sku]"
                    @click="handleSubscribe(module.sku)"
                  >
                    {{
                      module.is_active
                        ? $t('BILLING.PAGE.STORE.INCLUDED')
                        : isIspSku(module.sku) && activeIspIntegration
                          ? $t('BILLING.PAGE.STORE.BLOCKED')
                          : $t('BILLING.PAGE.STORE.SUBSCRIBE')
                    }}
                  </ButtonV4>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- INVOICES TAB -->
        <!-- SUBSCRIPTIONS TAB -->
        <div
          v-if="currentTab === 'subscriptions'"
          class="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden"
        >
          <div class="p-5 border-b border-slate-200 dark:border-slate-800">
            <h2
              class="text-lg font-semibold text-slate-800 dark:text-slate-100"
            >
              {{ $t('BILLING.PAGE.STORE.SUBSCRIPTIONS_TITLE') }}
            </h2>
            <p class="text-sm text-slate-500 mt-1">
              {{ $t('BILLING.PAGE.STORE.SUBSCRIPTIONS_DESCRIPTION') }}
            </p>
          </div>
          <div class="p-5">
            <div v-if="subscriptions.length === 0" class="text-center py-8">
              <span
                class="i-lucide-box text-4xl text-slate-300 dark:text-slate-600 mb-3 block"
              />
              <p class="text-slate-500">
                {{ $t('BILLING.PAGE.STORE.NO_SUBSCRIPTIONS') }}
              </p>
            </div>
            <div v-else class="overflow-x-auto">
              <table
                class="min-w-[680px] w-full text-left text-sm text-slate-600 dark:text-slate-300"
              >
                <thead>
                  <tr class="bg-slate-50 dark:bg-slate-800/50">
                    <th class="py-3 px-4 font-medium rounded-l-lg">
                      {{ $t('BILLING.PAGE.STORE.DESCRIPTION') }}
                    </th>
                    <th class="py-3 px-4 font-medium">{{ $t('BILLING.PAGE.STORE.VALUE') }}</th>
                    <th class="py-3 px-4 font-medium">{{ $t('BILLING.PAGE.STORE.NEXT_BILLING') }}</th>
                    <th class="py-3 px-4 font-medium">{{ $t('BILLING.PAGE.STORE.STATUS') }}</th>
                    <th class="py-3 px-4 font-medium text-right rounded-r-lg">
                      {{ $t('BILLING.PAGE.STORE.ACTION') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="sub in subscriptions"
                    :key="sub.id"
                    class="border-b border-slate-100 dark:border-slate-800 last:border-0"
                  >
                    <td class="py-4 px-4">{{ sub.description }}</td>
                    <td class="py-4 px-4 font-medium">R$ {{ sub.value }}</td>
                    <td class="py-4 px-4">{{ sub.nextDueDate }}</td>
                    <td class="py-4 px-4">
                      <span
                        :class="{
                          'bg-emerald-100 text-emerald-700':
                            sub.status === 'ACTIVE',
                          'bg-slate-100 text-slate-500':
                            sub.status === 'INACTIVE',
                        }"
                        class="px-2.5 py-1 rounded-full text-xs font-semibold"
                      >
                        {{ sub.status === 'ACTIVE' ? $t('BILLING.PAGE.STORE.ACTIVE') : $t('BILLING.PAGE.STORE.CANCELED') }}
                      </span>
                    </td>
                    <td class="py-4 px-4 text-right">
                      <button
                        v-if="sub.status === 'ACTIVE'"
                        class="text-rose-600 hover:text-rose-800 text-sm font-medium"
                        @click="cancelSubscription(sub.id)"
                      >
                        {{ $t('BILLING.PAGE.STORE.CANCEL') }}
                      </button>
                      <span v-else class="text-slate-400 text-xs"
                        >{{ $t('BILLING.PAGE.STORE.VALID_UNTIL', { date: sub.nextDueDate }) }}</span
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- INVOICES TAB -->
        <BillingInvoices
          v-if="currentTab === 'invoices'"
          :invoices="invoices"
          :unlinked-tax-invoices="unlinkedTaxInvoices"
          @openSendModal="openSendModal"
        />

        <!-- PREFERENCES TAB -->
        <div
          v-if="currentTab === 'preferences'"
          class="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6"
        >
          <h2
            class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2"
          >
            {{ $t('BILLING.PAGE.STORE.BILLING_PREFERENCES_TITLE') }}
          </h2>
          <p class="text-sm text-slate-500 mb-6">
            {{ $t('BILLING.PAGE.STORE.BILLING_PREFERENCES_DESCRIPTION') }}
          </p>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mb-6">
            <label
              :class="{
                'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500':
                  billingPreferences === 'email',
                'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300':
                  billingPreferences !== 'email',
              }"
              class="relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none transition-all"
            >
              <input
                v-model="billingPreferences"
                type="radio"
                value="email"
                class="sr-only"
              />
              <span class="flex flex-1">
                <span class="flex flex-col w-full">
                  <span
                    class="block text-sm font-medium text-slate-900 dark:text-white flex items-center gap-2"
                  >
                    <span class="i-lucide-mail text-slate-400" />
                    {{ $t('BILLING.PAGE.STORE.EMAIL') }}
                  </span>
                  <span
                    class="mt-1 flex items-center text-sm text-slate-500 dark:text-slate-400"
                    >{{ $t('BILLING.PAGE.STORE.EMAIL_RECEIPTS') }}</span
                  >

                  <div
                    v-if="billingPreferences === 'email'"
                    class="mt-3"
                    @click.stop
                  >
                    <label
                      class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
                      >{{ $t('BILLING.PAGE.STORE.WHICH_EMAIL') }}</label
                    >
                    <input
                      v-model="billingEmail"
                      type="email"
                      placeholder="seu@email.com"
                      class="w-full text-sm rounded-md border-slate-300 dark:border-slate-600 dark:bg-slate-800 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                    />
                  </div>
                </span>
              </span>
              <span
                v-if="billingPreferences === 'email'"
                class="i-lucide-check-circle-2 text-blue-600 text-xl absolute right-4 top-4"
              />
            </label>

            <label
              :class="{
                'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 ring-1 ring-emerald-500':
                  billingPreferences === 'whatsapp',
                'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300':
                  billingPreferences !== 'whatsapp',
              }"
              class="relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none transition-all"
            >
              <input
                v-model="billingPreferences"
                type="radio"
                value="whatsapp"
                class="sr-only"
              />
              <span class="flex flex-1">
                <span class="flex flex-col w-full">
                  <span
                    class="block text-sm font-medium text-slate-900 dark:text-white flex items-center gap-2"
                  >
                    <span class="i-lucide-message-circle text-emerald-500" />
                    WhatsApp
                  </span>
                  <span
                    class="mt-1 flex items-center text-sm text-slate-500 dark:text-slate-400"
                    >{{ $t('BILLING.PAGE.STORE.PIX_RECEIPTS') }}</span
                  >

                  <div
                    v-if="billingPreferences === 'whatsapp'"
                    class="mt-3"
                    @click.stop
                  >
                    <label
                      class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
                      >{{ $t('BILLING.PAGE.STORE.WHICH_PHONE') }}</label
                    >
                    <input
                      v-model="billingPhone"
                      type="tel"
                      placeholder="5511999999999"
                      class="w-full text-sm rounded-md border-slate-300 dark:border-slate-600 dark:bg-slate-800 focus:border-emerald-500 focus:ring-emerald-500 shadow-sm"
                    />
                  </div>
                </span>
              </span>
              <span
                v-if="billingPreferences === 'whatsapp'"
                class="i-lucide-check-circle-2 text-emerald-600 text-xl absolute right-4 top-4"
              />
            </label>
          </div>

          <ButtonV4
            color="blue"
            class="w-full max-w-lg justify-center transition-transform active:scale-95"
            :is-loading="isSavingPreferences"
            @click="savePreferences"
          >
            {{ $t('BILLING.PAGE.STORE.SAVE_PREFERENCES') }}
          </ButtonV4>
        </div>

        <!-- Plan Upgrade Modal -->
        <div
          v-if="showPlanUpgradeModal"
          class="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4"
        >
          <div
            class="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-4xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col"
          >
            <div
              class="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50"
            >
              <h3 class="text-xl font-bold text-slate-800 dark:text-slate-100">
                {{ $t('BILLING.PAGE.PLAN_MODAL.TITLE') }}
              </h3>
              <button
                class="text-slate-400 hover:text-slate-600 transition-colors"
                @click="showPlanUpgradeModal = false"
              >
                <span class="i-lucide-x text-2xl" />
              </button>
            </div>
            <div class="p-4 sm:p-6 overflow-y-auto">
              <div
                v-if="availablePlans.length === 0"
                class="text-center py-12 text-slate-500"
              >
                {{ $t('BILLING.PAGE.PLAN_MODAL.EMPTY') }}
              </div>
              <div
                v-else
                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                <div
                  v-for="plan in availablePlans"
                  :key="plan.id"
                  class="border border-slate-200 dark:border-slate-700 rounded-xl p-6 flex flex-col hover:border-blue-500 transition-colors shadow-sm relative"
                >
                  <h4
                    class="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2"
                  >
                    {{ plan.name }}
                  </h4>
                  <div class="text-3xl font-black text-blue-600 mb-6">
                    R$
                    {{
                      parseFloat(plan.monthly_price || 0)
                        .toFixed(2)
                        .replace('.', ',')
                    }}<span class="text-sm font-normal text-slate-500"
                      >{{ $t('BILLING.PAGE.MONTH') }}</span
                    >
                  </div>
                  <ul class="space-y-3 mb-8 flex-1">
                    <li
                      class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
                    >
                      <span class="i-lucide-check text-emerald-500" />
                      {{
                        plan.max_users != null
                          ? plan.max_users
                          : $t('BILLING.PAGE.UNLIMITED')
                      }}
                      {{ $t('BILLING.PAGE.PLAN_MODAL.USERS') }}
                    </li>
                    <li
                      class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
                    >
                      <span class="i-lucide-check text-emerald-500" />
                      {{
                        plan.max_campaigns != null
                          ? plan.max_campaigns
                          : $t('BILLING.PAGE.UNLIMITED')
                      }}
                      {{ $t('BILLING.PAGE.PLAN_MODAL.CAMPAIGNS') }}
                    </li>
                    <li
                      v-if="plan.enabled_modules?.api"
                      class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
                    >
                      <span class="i-lucide-check text-emerald-500" />
                      {{ $t('BILLING.PAGE.PLAN_MODAL.API_ACCESS') }}
                    </li>
                  </ul>
                  <button
                    :disabled="plan.name === planName"
                    :class="{
                      'opacity-50 cursor-not-allowed bg-slate-400':
                        plan.name === planName,
                      'bg-blue-600 hover:bg-blue-700': plan.name !== planName,
                    }"
                    class="w-full text-white font-semibold py-2.5 rounded-lg transition-colors"
                    @click="updatePlan(plan.id)"
                  >
                    {{
                      plan.name === planName
                        ? $t('BILLING.PAGE.PLAN_MODAL.CURRENT_PLAN')
                        : $t('BILLING.PAGE.PLAN_MODAL.SUBSCRIBE', {
                            planName: plan.name,
                          })
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <woot-modal
      v-model:show="showUpgradeModal"
      :on-close="() => (showUpgradeModal = false)"
      show-close-button
    >
      <div class="p-6">
        <h2 class="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">
          {{
            upgradeAutoCancel.length > 0 || upgradeOptionalCancel.length > 0
              ? $t('BILLING.UPGRADE.REVIEW_TITLE')
              : $t('BILLING.UPGRADE.CONFIRM_TITLE')
          }}
        </h2>
        <p
          v-if="
            upgradeAutoCancel.length > 0 || upgradeOptionalCancel.length > 0
          "
          class="text-slate-600 dark:text-slate-300 mb-6 text-sm"
        >
          {{
            $t('BILLING.UPGRADE.REVIEW_MESSAGE', {
              planName: upgradePlanData?.name,
            })
          }}
        </p>
        <p v-else class="text-slate-600 dark:text-slate-300 mb-6 text-sm">
          {{
            $t('BILLING.UPGRADE.CONFIRM_MESSAGE', {
              planName: upgradePlanData?.name,
            })
          }}
        </p>

        <!-- Auto Cancel -->
        <div
          v-if="upgradeAutoCancel.length > 0"
          class="mb-6 bg-rose-50 dark:bg-rose-900/20 rounded-lg p-4 border border-rose-100 dark:border-rose-900/50"
        >
          <h3
            class="font-bold text-rose-800 dark:text-rose-300 flex items-center gap-2 mb-2"
          >
            <span class="i-lucide-info text-lg" />
            {{ $t('BILLING.PAGE.STORE.AUTO_REPLACEMENT') }}
          </h3>
          <p class="text-sm text-rose-700 dark:text-rose-400 mb-3">
            {{ $t('BILLING.PAGE.STORE.AUTO_REPLACEMENT_DESCRIPTION') }}
          </p>
          <ul class="space-y-1">
            <li
              v-for="sub in upgradeAutoCancel"
              :key="sub.id"
              class="text-sm font-medium text-slate-700 dark:text-slate-300 flex justify-between"
            >
              <span>{{ sub.description }}</span>
              <span class="text-slate-400 line-through"
                >R$ {{ sub.value }}</span
              >
            </li>
          </ul>
        </div>

        <!-- Optional Cancel -->
        <div
          v-if="upgradeOptionalCancel.length > 0"
          class="mb-6 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-100 dark:border-amber-900/50"
        >
          <h3
            class="font-bold text-amber-800 dark:text-amber-300 flex items-center gap-2 mb-2"
          >
            <span class="i-lucide-help-circle text-lg" />
            {{ $t('BILLING.PAGE.STORE.QUANTITATIVE_SUBSCRIPTIONS') }}
          </h3>
          <p class="text-sm text-amber-700 dark:text-amber-400 mb-3">
            {{ $t('BILLING.PAGE.STORE.QUANTITATIVE_DESCRIPTION') }}
          </p>
          <div class="space-y-2">
            <label
              v-for="sub in upgradeOptionalCancel"
              :key="sub.id"
              class="flex items-center gap-3 p-2 rounded-md hover:bg-amber-100 dark:hover:bg-amber-900/40 cursor-pointer"
            >
              <input
                v-model="selectedOptionalCancel"
                type="checkbox"
                :value="sub.id"
                class="rounded text-amber-600 focus:ring-amber-500 border-amber-300"
              />
              <div class="flex flex-col">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >{{ sub.description }}</span
                >
                <span class="text-xs text-slate-500"
                  >R$ {{ sub.value }}{{ $t('BILLING.PAGE.STORE.MONTH') }}</span
                >
              </div>
            </label>
          </div>
        </div>

        <div class="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-8">
          <ButtonV4 variant="outline" @click="showUpgradeModal = false">
            {{ $t('BILLING.PAGE.STORE.CANCEL') }}
          </ButtonV4>
          <ButtonV4
            color="teal"
            :is-loading="isSubscribing['upgrade']"
            @click="confirmUpgrade"
          >
            {{ $t('BILLING.PAGE.STORE.CONFIRM_CHANGE') }}
          </ButtonV4>
        </div>
      </div>
    </woot-modal>

    <woot-modal
      v-model:show="showConfirmModal"
      :on-close="() => (showConfirmModal = false)"
      show-close-button
    >
      <div class="p-6">
        <div
          class="flex items-center gap-3 mb-4 text-slate-800 dark:text-slate-100"
        >
          <div
            class="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400"
          >
            <span class="i-heroicons-shopping-cart text-xl" />
          </div>
          <h2 class="text-xl font-bold">
            {{ $t('BILLING.PAGE.STORE.CONFIRM_SUBSCRIPTION') }}
          </h2>
        </div>

        <p class="text-slate-600 dark:text-slate-300 mb-6 text-base">
          {{
            $t('BILLING.PAGE.STORE.ABOUT_TO_ADD', {
              quantity: confirmModalData.quantity,
              name: confirmModalData.name,
            })
          }}
        </p>

        <div
          v-if="
            confirmModalData.missingDeps && confirmModalData.missingDeps.length
          "
          class="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6"
        >
          <p
            class="text-sm text-amber-800 dark:text-amber-200 font-medium mb-1"
          >
            ⚠️ {{ $t('BILLING.PAGE.STORE.DEPENDENCY_REQUIRED') }}
          </p>
          <p class="text-xs text-amber-700 dark:text-amber-300">
            {{ $t('BILLING.PAGE.STORE.DEPENDENCY_DESCRIPTION') }}
            <strong>{{
              confirmModalData.missingDeps
                .map(dep => moduleName(dep) || dep)
                .join(', ')
            }}</strong>
          </p>
        </div>

        <div
          class="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 mb-6 border border-slate-200 dark:border-slate-700 flex justify-between items-center"
        >
          <span class="text-slate-500 dark:text-slate-400 font-medium"
            >{{ $t('BILLING.PAGE.STORE.ADDITIONAL_VALUE') }}</span
          >
          <span class="text-xl font-black text-slate-800 dark:text-slate-100"
            >R$ {{ confirmModalData.total?.toFixed(2).replace('.', ',')
            }}<span class="text-sm text-slate-500 font-normal">{{ $t('BILLING.PAGE.STORE.MONTH') }}</span></span
          >
        </div>

        <p class="text-sm text-slate-500 dark:text-slate-400 mb-6 italic">
          {{ $t('BILLING.PAGE.STORE.PRORATED_DESCRIPTION') }}
        </p>

        <div class="flex flex-col-reverse sm:flex-row justify-end gap-3">
          <ButtonV4
            color="slate"
            variant="ghost"
            @click="showConfirmModal = false"
          >
            {{ $t('BILLING.PAGE.STORE.CANCEL') }}
          </ButtonV4>
          <ButtonV4 color="blue" @click="executeSubscription">
            {{ $t('BILLING.PAGE.STORE.CONFIRM_AND_SUBSCRIBE') }}
          </ButtonV4>
        </div>
      </div>
    </woot-modal>

    <woot-modal
      v-model:show="showSendModal"
      :on-close="() => (showSendModal = false)"
      show-close-button
    >
      <div class="p-6">
        <div
          class="flex items-center gap-3 mb-4 text-slate-800 dark:text-slate-100"
        >
          <div
            class="w-10 h-10 rounded-full flex items-center justify-center"
            :class="
              sendModalData.type === 'email'
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'
                : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600'
            "
          >
            <span
              :class="
                sendModalData.type === 'email'
                  ? 'i-lucide-mail'
                  : 'i-lucide-message-circle'
              "
              class="text-xl"
            />
          </div>
          <h2 class="text-xl font-bold">
            {{ $t('BILLING.PAGE.STORE.SEND_INVOICE') }}
          </h2>
        </div>

        <p class="text-slate-600 dark:text-slate-300 mb-6 text-base">
          {{
            $t('BILLING.PAGE.STORE.SEND_INVOICE_QUESTION', {
              target:
                sendModalData.type === 'email'
                  ? $t('BILLING.PAGE.STORE.EMAIL')
                  : $t('BILLING.PAGE.STORE.WHATSAPP_NUMBER'),
            })
          }}
        </p>

        <div class="mb-6">
          <input
            v-if="sendModalData.type === 'email'"
            v-model="sendModalData.target"
            type="email"
            placeholder="seu@email.com"
            class="w-full text-base p-3 rounded-lg border-slate-300 dark:border-slate-600 dark:bg-slate-800 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
          />
          <input
            v-else
            v-model="sendModalData.target"
            type="tel"
            placeholder="5511999999999"
            class="w-full text-base p-3 rounded-lg border-slate-300 dark:border-slate-600 dark:bg-slate-800 focus:border-emerald-500 focus:ring-emerald-500 shadow-sm"
          />
        </div>

        <div class="flex flex-col-reverse sm:flex-row justify-end gap-3">
          <ButtonV4
            color="slate"
            variant="ghost"
            @click="showSendModal = false"
          >
            {{ $t('BILLING.PAGE.STORE.CANCEL') }}
          </ButtonV4>
          <ButtonV4
            :color="sendModalData.type === 'email' ? 'blue' : 'teal'"
            :is-loading="isSendingInvoice"
            @click="sendInvoice"
          >
            {{ $t('BILLING.PAGE.STORE.SEND') }}
          </ButtonV4>
        </div>
      </div>
    </woot-modal>
  </SettingsLayout>
</template>
